import { getJson } from './client';

export interface TaskSelfQuery {
  page?: number;
  pageSize?: number;
  startTimestamp?: number | null;
  endTimestamp?: number | null;
  taskId?: string | null;
  platform?: string | null;
  status?: string | null;
}

export interface TaskSelfItem {
  id: number;
  task_id: string;
  platform: string;
  action: string;
  status: string;
  created_at?: number | null;
  updated_at?: number | null;
  progress?: string | null;
  submit_time?: number | null;
  start_time?: number | null;
  finish_time?: number | null;
  quota?: number | null;
  fail_reason?: string | null;
  properties?: Record<string, unknown> | null;
  data?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface TaskSelfResponseData {
  page: number;
  page_size: number;
  total: number;
  start_timestamp?: number | null;
  end_timestamp?: number | null;
  items: TaskSelfItem[];
}

export interface TaskSelfResponse {
  success: boolean;
  message: string;
  data?: TaskSelfResponseData | null;
  [key: string]: unknown;
}

const buildQueryString = (params: TaskSelfQuery): string => {
  const search = new URLSearchParams();
  if (params.page && params.page > 0) {
    search.set('p', String(params.page));
  }
  if (params.pageSize && params.pageSize > 0) {
    search.set('page_size', String(params.pageSize));
  }
  if (params.startTimestamp && params.startTimestamp > 0) {
    search.set('start_timestamp', String(Math.trunc(params.startTimestamp)));
  }
  if (params.endTimestamp && params.endTimestamp > 0) {
    search.set('end_timestamp', String(Math.trunc(params.endTimestamp)));
  }
  const appendStringParam = (key: string, value?: string | null) => {
    if (value === null || value === undefined) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    search.set(key, trimmed);
  };
  appendStringParam('task_id', params.taskId);
  appendStringParam('platform', params.platform);
  appendStringParam('status', params.status);
  return search.toString();
};

export const fetchSelfTasks = async (query: TaskSelfQuery = {}): Promise<TaskSelfResponse> => {
  const queryString = buildQueryString(query);
  const endpoint = queryString ? `/api/workers-proxy/task-self?${queryString}` : '/api/workers-proxy/task-self';

  return getJson<TaskSelfResponse>(endpoint, {
    resolveEndpoint: false,
    cache: 'no-store',
  });
};

export const fetchTaskById = async (taskId: string): Promise<TaskSelfItem | null> => {
  const trimmed = taskId?.trim();
  if (!trimmed) {
    return null;
  }
  const response = await fetchSelfTasks({
    taskId: trimmed,
    page: 1,
    pageSize: 1,
  });
  if (!response.success) {
    return null;
  }
  const items = response.data?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  return items[0] ?? null;
};
